// src/utils/floorPlanMesh.js
//
// Kat planı (floor plan) Mesh container'ı için fabrika.
//
// Mesh-FP mimarisi (bkz. docs/mesh-fp-mimari.md):
//   - Her kat planı = 1 Three.js Mesh (PlaneGeometry).
//   - Tüm fp meshleri **aynı boyutta** = projedeki en büyük fp'nin (width, height).
//   - Mesh local (0,0) = sol-alt köşe (CAD/DXF konvansiyonu).
//   - DXF mimari + tesisat + wire = mesh'in child'ı, local koordinatlarda.
//   - Layout/drag sırasında sadece mesh.position değişir; çocuklar parent-matrix
//     ile otomatik takip eder.

import * as THREE from 'three';

/** Three.js layer ID'leri.
 *
 * Three.js'te Object3D.layers.set(N) ile mesh N. layer'a atanır. Camera ve
 * raycaster `.enableLayer(N)` ile sadece o layer'ı görür / hit eder.
 *
 * MIMARI_LAYER (0): DXF mimari içeriği, frame'ler, doküman çerçeveleri.
 *                   Three.js default layer — özel set gerekmez.
 * TESISAT_LAYER (1): Tesisat komponentleri (priz, panel, makine, lamba,
 *                    anahtar, supplyPoint vb.) + wire'lar (Aşama 4).
 *                    Ayrı layer'da olmasının sebebi: pafta render'ında
 *                    seçici görünürlük (örn. Topraklama paftası sadece
 *                    topraklama tesisatı), raycaster filtreleme.
 */
export const MIMARI_LAYER = 0;
export const TESISAT_LAYER = 1;

/**
 * Camera ve raycaster için layer setup helper'ı.
 * Genelde uygulama başlangıcında 1 kez çağrılır.
 * @param {THREE.Camera} camera
 * @param {THREE.Raycaster} [raycaster]  Opsiyonel — raycaster da set edilebilir
 */
export function enableAllFpLayers(camera, raycaster) {
  if (camera?.layers) {
    camera.layers.enable(MIMARI_LAYER);
    camera.layers.enable(TESISAT_LAYER);
  }
  if (raycaster?.layers) {
    raycaster.layers.enable(MIMARI_LAYER);
    raycaster.layers.enable(TESISAT_LAYER);
  }
}

/**
 * Kat planı container Mesh'i oluşturur.
 *
 * @param {Object} params
 * @param {number} params.width    Mesh genişliği (proje genelinde uniform — selectProjectMeshSize'dan).
 * @param {number} params.height   Mesh yüksekliği (proje genelinde uniform).
 * @param {string} params.floorPlanId  Kat planı ID'si (userData'ya yazılır).
 * @param {Object} [params.debug]  Debug çerçevesi seçenekleri.
 * @param {boolean} [params.debug.visible=false]  Mesh'in görünür (yarı saydam) olup olmadığı.
 * @param {number}  [params.debug.color=0x00aaff]
 * @param {number}  [params.debug.opacity=0.05]
 * @returns {THREE.Mesh}
 */
export function createFloorPlanMesh({
  width,
  height,
  floorPlanId,
  debug = {},
} = {}) {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    throw new Error(`createFloorPlanMesh: geçersiz boyut (${width} × ${height})`);
  }
  if (!floorPlanId) {
    throw new Error('createFloorPlanMesh: floorPlanId zorunlu');
  }

  // PlaneGeometry default: merkez (0,0). Sol-alt origin için geometry.translate.
  const geometry = new THREE.PlaneGeometry(width, height);
  geometry.translate(width / 2, height / 2, 0);

  const { visible = false, color = 0x00aaff, opacity = 0.05 } = debug;
  const material = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: visible ? opacity : 0,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.layers.set(MIMARI_LAYER);
  mesh.renderOrder = -1; // arka planda; çocuklar üstte çizilsin
  // Container mesh raycaster'a takılmasın — tesisat ekleme, panel tıklama,
  // gizmo seçimi gibi etkileşimler çocuk mesh'lere ulaşmalı. Three.js'in
  // resmi yöntemi: Object3D.raycast'i no-op'a çevir.
  mesh.raycast = () => {};
  mesh.userData = {
    ...mesh.userData,
    isFloorPlanMesh: true,
    floorPlanId,
    meshWidth: width,
    meshHeight: height,
    // Aşama 2+'da useElectricalLayout cleanup'ı bu flag'i okuyup mesh'in
    // çocuklarını dispose etmeden saklayacak.
    preserveOnLayout: true,
  };
  mesh.name = `fpMesh_${floorPlanId}`;

  return mesh;
}

/**
 * Sahnedeki bir Object3D'nin floor plan Mesh container'ını bulur.
 * @param {THREE.Object3D} obj  Aranan child.
 * @returns {THREE.Mesh|null}   En yakın atası olan fp mesh, yoksa null.
 */
export function findContainingFloorPlanMesh(obj) {
  let cur = obj;
  while (cur) {
    if (cur.userData?.isFloorPlanMesh) return cur;
    cur = cur.parent;
  }
  return null;
}

/**
 * Sahnede verilen floorPlanId'ye sahip CANLI (clone, sahneye eklenmiş, layout
 * offset uygulanmış) kat planı grubunu bulur.
 *
 * Önemli: Redux'taki plan.group orijinal source grup'tur — sahneye eklenmemiş,
 * matrixWorld ≈ identity. useElectricalLayout `clone(true)` ile kopyalayıp
 * `isLiveFloorPlanGroup` tag'i ile sahneye ekler. Kolon detection, raycaster
 * arama, vd. anlık world pozisyonuna ihtiyaç duyduğunda bu canlı kopyayı
 * kullanmalı.
 *
 * @param {THREE.Object3D} sceneRoot
 * @param {string} floorPlanId
 * @returns {THREE.Group|null}
 */
export function findLiveFloorPlanGroup(sceneRoot, floorPlanId) {
  if (!sceneRoot || !floorPlanId) return null;
  let found = null;
  sceneRoot.traverse((obj) => {
    if (found) return;
    if (obj.userData?.isLiveFloorPlanGroup && obj.userData?.floorPlanId === floorPlanId) {
      found = obj;
    }
  });
  return found;
}

/**
 * Sahnede verilen floorPlanId'ye sahip fp mesh'i bulur.
 * @param {THREE.Object3D} sceneRoot  Sahne kökü (veya alt köke göre arama).
 * @param {string} floorPlanId
 * @returns {THREE.Mesh|null}
 */
export function findFloorPlanMeshById(sceneRoot, floorPlanId) {
  if (!sceneRoot || !floorPlanId) return null;
  let found = null;
  sceneRoot.traverse((obj) => {
    if (found) return;
    if (obj.userData?.isFloorPlanMesh && obj.userData?.floorPlanId === floorPlanId) {
      found = obj;
    }
  });
  return found;
}

/**
 * Tesisat mesh'ini fpMesh'in child'ı olarak yerleştirir.
 *
 * Kullanım: scene.add(tesisatMesh) yerine attachTesisatToFloorPlanMesh(scene,
 * tesisatMesh, floorPlanId). Helper:
 *   - floorPlanId varsa: scene'de fpMesh'i bul → mesh.attach(tesisatMesh)
 *     (Three.js attach world transform'u korur, mesh'in görsel konumu aynı kalır)
 *     + tesisat mesh'i TESISAT_LAYER'a koy (mimari layer'dan ayır)
 *   - fpMesh bulunamazsa: scene.add(tesisatMesh) (fallback, legacy davranış)
 *
 * Mesh-FP mimarisinin (docs/mesh-fp-mimari.md) Aşama 3 entegrasyonu:
 * tesisat bu sayede fpMesh ile birlikte hareket eder (drag/layout sırasında
 * parent-matrix ile otomatik takip).
 *
 * @param {THREE.Scene} scene
 * @param {THREE.Object3D} tesisatMesh    Eklenecek tesisat mesh'i (örn. socket, panel)
 * @param {string|null} floorPlanId       Hangi fp'ye bağlanacak (null → scene fallback)
 * @returns {{mesh: THREE.Mesh|null, attached: boolean}}  fpMesh referansı + attach durumu
 */
export function attachTesisatToFloorPlanMesh(scene, tesisatMesh, floorPlanId) {
  if (!scene || !tesisatMesh) return { mesh: null, attached: false };
  if (floorPlanId) {
    tesisatMesh.userData = { ...tesisatMesh.userData, floorPlanId };
  }
  const fpMesh = floorPlanId ? findFloorPlanMeshById(scene, floorPlanId) : null;
  if (fpMesh) {
    fpMesh.attach(tesisatMesh);
    return { mesh: fpMesh, attached: true };
  }
  scene.add(tesisatMesh);
  return { mesh: null, attached: false };
}

/**
 * Post-render tesisat sweep: sahnede `userData.type` tesisat tiplerinden olan
 * tüm mesh'leri tarar, parent fpMesh değilse attach eder.
 *
 * Kullanım: useElectricalLayout effect SONUNDA (scene.add(mainFrameGroup)
 * sonrası) çağrılır. Mevcut wiring akışları (useComponentWiring vs.) socket'i
 * scene'e farklı yollardan ekliyor — bu sweep tek noktada yakalar.
 *
 * Idempotent: zaten fpMesh-child olan mesh tekrar attach edilmez.
 *
 * @param {THREE.Scene} scene
 * @param {Function} getFloorPlanIdForPos  (worldPos) => floorPlanId|null helper
 *                                          (örn. getActiveFloorPlanId + documentBounds)
 * @returns {{attached: number, skipped: number, missing: number}}
 */
const TESISAT_TYPES = new Set([
  // Komponentler
  'socket', 'mainPanel', 'electricalPanel', 'supplyBox', 'supplyPoint',
  'machine',
  'lightFixture', 'LightFixture', // varyant: büyük/küçük L
  'lightSwitch', 'LightSwitch',
  'bell', 'bellButton', 'bellTrafo',
  // Kablo bacası ve bileşenleri
  'cableShaft', 'cableShaftConnection',
  'shaftDiagonalLine', 'shaftExit', 'shaftExitPoint',
  // Buat'lar (priz/anahtar/aydınlatma kablo bağlantı noktaları)
  'socketBuat', 'buatCircle', 'buatSquare',
  'lightBuat', 'lightingBuat', 'buatLightingCircle', 'buatLightingSquare',
  // Wire marker / label
  'wireMarker', 'wireLabel', 'wireTitle',
  // Kat geçişleri (multi-floor)
  'floorTransitionArrow', 'floorTransitionWire', 'incomingFloorTransitionArrow',
  // Trafo çıkışları (zil/aydınlatma trafo bağlantıları)
  'trafoOutput', 'trafo_out',
  // Topraklama
  'groundingBuatLabel', 'groundingLabel', 'groundingStrake',
]);

/**
 * Tesisat mesh olup olmadığını kontrol eder. Komponent tipleri (TESISAT_TYPES)
 * + wire (type 'wire' prefix'i ile başlayan) + role 'wireMarker'.
 */
function isTesisatMesh(obj) {
  const ud = obj?.userData;
  if (!ud) return false;
  const t = ud.type;
  if (typeof t === 'string') {
    if (TESISAT_TYPES.has(t)) return true;
    // wire tipleri: wireSocket, wireLight, wireMachine, wireBell, wireMainPanel,
    // wireElectricalPanel, wireFixture, wireTitle, wireLabel, vb. Tümü 'wire'
    // prefix'i ile başlar.
    if (t.startsWith('wire')) return true;
  }
  // Bazı marker'lar userData.role = 'wireMarker' kullanıyor (type değil)
  if (ud.role === 'wireMarker') return true;
  // Gerçek elektrik wire'ları (priz/pano hatları) çoğu zaman type='polyline'/
  // 'curvedLine' taşır AMA id'leri 'wire'/'feeder' ile başlar (wireMainFeeder,
  // feederSocket, wirePanelFeeder, ...). Bunlar da kat planı içeriğidir → fpMesh
  // child olmalı ki kat planı taşınınca birlikte hareket etsinler. Önceden type
  // 'polyline' olduğu için kapsanmıyor, scene root'ta kalıp kat planından
  // ayrılıyorlardı. DXF KAPSANMAZ: DXF çizgileri source==='dxf' taşır ve id'leri
  // wire/feeder değildir → 489-çöp riski yok.
  const id = ud.id;
  if (typeof id === 'string' && /^(wire|feeder)/i.test(id)
      && ud.source !== 'dxf' && ud._userDrawn !== true) {
    return true;
  }
  return false;
}

// Cleanup tarafından da kullanılabilsin diye export
export { isTesisatMesh };

// Bir nesnenin fpMesh atasını bul (yoksa null).
function findFpMeshAncestor(obj, scene) {
  let p = obj?.parent;
  while (p && p !== scene) {
    if (p.userData?.isFloorPlanMesh) return p;
    p = p.parent;
  }
  return null;
}

// Nesnenin atalarından biri (kendisi hariç) tesisat mı? Bir tesisat'ın PARÇASI
// (örn. cableShaft group'unun fillMesh/border/diagonal child'ları — group.userData'yı
// miras aldıkları için type='cableShaft' taşırlar) ayrı taşınmamalı; üst tesisat'la
// birlikte hareket eder. Aksi halde rescue/sweep parçaları group'tan koparıp yanlış
// (group-local) konuma yerleştiriyor → cableShaft parçalanıp kat planından kopuyor.
export function hasTesisatAncestor(obj, scene) {
  let p = obj?.parent;
  while (p && p !== scene) {
    if (isTesisatMesh(p)) return true;
    p = p.parent;
  }
  return false;
}

// Nesneyi fpMesh'e yerleştir — _savedLocalPos varsa local koru, yoksa world koru.
function placeInFpMesh(obj, fpMesh) {
  const savedLocal = obj.userData._savedLocalPos;
  if (savedLocal) {
    fpMesh.add(obj);
    obj.position.set(savedLocal.x, savedLocal.y, savedLocal.z);
    delete obj.userData._savedLocalPos;
  } else {
    fpMesh.attach(obj);
  }
  if (fpMesh.userData?.floorPlanId) obj.userData.floorPlanId = fpMesh.userData.floorPlanId;
}

export function sweepTesisatToFloorPlanMeshes(scene, getFloorPlanIdForPos) {
  if (!scene) return { attached: 0, skipped: 0, missing: 0 };
  let attached = 0, skipped = 0, missing = 0;
  const candidates = [];
  const byId = new Map();
  scene.traverse((obj) => {
    if (obj.userData?.id && !byId.has(obj.userData.id)) byId.set(obj.userData.id, obj);
    if (!isTesisatMesh(obj)) return;
    // Tesisat PARÇALARINI (cableShaft fillMesh/border/diagonal gibi) atla — sadece
    // en dıştaki tesisat'ı taşı; parçalar onunla birlikte hareket eder.
    if (hasTesisatAncestor(obj, scene)) return;
    candidates.push(obj);
  });
  // wireMarker'ları en sona al: parent wire'lar önce fpMesh'e girsin ki
  // marker'lar parent wire'ın fpMesh'ini referans alabilsin.
  const isMarker = (o) => o.userData?.role === 'wireMarker';
  candidates.sort((a, b) => (isMarker(a) ? 1 : 0) - (isMarker(b) ? 1 : 0));
  for (const obj of candidates) {
    let p = obj.parent;
    let alreadyInFpMesh = false;
    while (p && p !== scene) {
      if (p.userData?.isFloorPlanMesh) { alreadyInFpMesh = true; break; }
      p = p.parent;
    }
    if (alreadyInFpMesh) { skipped++; continue; }
    // wireMarker: floorPlanId taşımaz; PARENT WIRE'ın fpMesh'ine bağla. Wire
    // (id wire/feeder) bu sweep'te zaten fpMesh'e taşınmış olur (marker'lar sona
    // sıralandı). Pozisyon-bazlı lookup layout değişiminde güvenilmez olduğu için
    // marker'ı doğrudan parent wire'ın fpMesh'ine ekliyoruz → kat planıyla hareket.
    if (isMarker(obj) && obj.userData?.parentWireId) {
      const pw = byId.get(obj.userData.parentWireId);
      const wireFpMesh = pw ? findFpMeshAncestor(pw, scene) : null;
      if (wireFpMesh) {
        placeInFpMesh(obj, wireFpMesh);
        attached++;
        continue;
      }
    }
    let floorPlanId = obj.userData?.floorPlanId || null;
    if (!floorPlanId && typeof getFloorPlanIdForPos === 'function') {
      obj.updateMatrixWorld(true);
      const px = obj.matrixWorld.elements[12];
      const py = obj.matrixWorld.elements[13];
      let worldPos = { x: px, y: py };
      const isNearOrigin = Math.abs(px) < 0.5 && Math.abs(py) < 0.5;
      if (isNearOrigin && obj.geometry) {
        if (!obj.geometry.boundingBox) {
          try { obj.geometry.computeBoundingBox(); } catch (_) {}
        }
        const bb = obj.geometry.boundingBox;
        if (bb && Number.isFinite(bb.min.x) && Number.isFinite(bb.max.x)) {
          const localCx = (bb.min.x + bb.max.x) / 2;
          const localCy = (bb.min.y + bb.max.y) / 2;
          const v = new THREE.Vector3(localCx, localCy, 0).applyMatrix4(obj.matrixWorld);
          if (Number.isFinite(v.x) && Number.isFinite(v.y)) worldPos = { x: v.x, y: v.y };
        }
      }
      floorPlanId = getFloorPlanIdForPos(worldPos) || null;
    }
    if (!floorPlanId) { missing++; continue; }
    const fpMesh = findFloorPlanMeshById(scene, floorPlanId);
    if (!fpMesh) { missing++; continue; }
    placeInFpMesh(obj, fpMesh);
    obj.userData.floorPlanId = floorPlanId;
    attached++;
  }
  return { attached, skipped, missing };
}

/**
 * Mesh-local koordinattan world koordinatına çevir.
 * @param {THREE.Mesh} mesh
 * @param {{x:number,y:number,z?:number}} localPos
 * @returns {THREE.Vector3}
 */
export function meshLocalToWorld(mesh, localPos) {
  const v = new THREE.Vector3(localPos.x ?? 0, localPos.y ?? 0, localPos.z ?? 0);
  mesh.updateWorldMatrix(true, false);
  return v.applyMatrix4(mesh.matrixWorld);
}

/**
 * World koordinatından verilen mesh'in local'ine çevir.
 * @param {THREE.Mesh} mesh
 * @param {{x:number,y:number,z?:number}} worldPos
 * @returns {{x:number,y:number,z:number}}
 */
export function worldToMeshLocal(mesh, worldPos) {
  const v = new THREE.Vector3(worldPos.x ?? 0, worldPos.y ?? 0, worldPos.z ?? 0);
  mesh.updateWorldMatrix(true, false);
  const inv = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
  v.applyMatrix4(inv);
  return { x: v.x, y: v.y, z: v.z };
}
